/**
 * Formatting Styles Service
 *
 * Provides formatting configurations for AI-generated content.
 * Converts style definitions to HTML templates that the AI should follow.
 */

// Style element configuration
export interface StyleElement {
  fontFamily: string;
  fontSize: string;
  fontWeight?: string;
  fontStyle?: string;
  textTransform?: string;
  textAlign?: string;
  textIndent?: string;
  marginTop?: string;
  marginBottom?: string;
  marginLeft?: string;
  marginRight?: string;
  lineHeight?: string;
  color?: string;
}

// Complete style definition
export interface FormattingStyle {
  name: string;
  description: string;
  font: {
    family: string;
    size: string;
    color: string;
  };
  lineSpacing: {
    normal: string;
    citation: string;
  };
  textAlign: string;
  paragraphIndent: string;
  elements: {
    h1: StyleElement;
    h2: StyleElement;
    h3?: StyleElement;
    p: StyleElement;
    blockquote: StyleElement;
    ul?: StyleElement;
    ol?: StyleElement;
    li?: StyleElement;
    [key: string]: StyleElement | undefined;
  };
}

// Available style names
export type StyleName = "abnt" | "juridico" | "contrato";

/**
 * Convert a StyleElement to inline CSS string
 */
function elementToInlineStyle(element: StyleElement): string {
  const styles: string[] = [];

  if (element.fontFamily) styles.push(`font-family: ${element.fontFamily}`);
  if (element.fontSize) styles.push(`font-size: ${element.fontSize}`);
  if (element.fontWeight) styles.push(`font-weight: ${element.fontWeight}`);
  if (element.fontStyle) styles.push(`font-style: ${element.fontStyle}`);
  if (element.textTransform) styles.push(`text-transform: ${element.textTransform}`);
  if (element.textAlign) styles.push(`text-align: ${element.textAlign}`);
  if (element.textIndent) styles.push(`text-indent: ${element.textIndent}`);
  if (element.marginTop) styles.push(`margin-top: ${element.marginTop}`);
  if (element.marginBottom) styles.push(`margin-bottom: ${element.marginBottom}`);
  if (element.marginLeft) styles.push(`margin-left: ${element.marginLeft}`);
  if (element.marginRight) styles.push(`margin-right: ${element.marginRight}`);
  if (element.lineHeight) styles.push(`line-height: ${element.lineHeight}`);
  if (element.color) styles.push(`color: ${element.color}`);

  return styles.join("; ");
}

/**
 * Pre-defined styles (loaded from YAML conceptually, hardcoded for runtime)
 */
const STYLES: Record<StyleName, FormattingStyle> = {
  abnt: {
    name: "ABNT - Padrão",
    description: "Formatação conforme normas ABNT para documentos acadêmicos e jurídicos",
    font: { family: "Verdana", size: "12pt", color: "#000000" },
    lineSpacing: { normal: "1.5", citation: "1.0" },
    textAlign: "justify",
    paragraphIndent: "1.25cm",
    elements: {
      h1: {
        fontFamily: "Verdana",
        fontSize: "12pt",
        fontWeight: "bold",
        textTransform: "uppercase",
        textAlign: "left",
        marginTop: "0",
        marginBottom: "1.5cm",
        lineHeight: "1.5",
      },
      h2: {
        fontFamily: "Verdana",
        fontSize: "12pt",
        fontWeight: "bold",
        textAlign: "left",
        marginTop: "1cm",
        marginBottom: "0.5cm",
        lineHeight: "1.5",
      },
      h3: {
        fontFamily: "Verdana",
        fontSize: "12pt",
        fontWeight: "bold",
        fontStyle: "italic",
        textAlign: "left",
        marginTop: "0.5cm",
        marginBottom: "0.5cm",
        lineHeight: "1.5",
      },
      p: {
        fontFamily: "Verdana",
        fontSize: "12pt",
        textAlign: "justify",
        textIndent: "1.25cm",
        marginTop: "0",
        marginBottom: "0",
        lineHeight: "1.5",
      },
      blockquote: {
        fontFamily: "Verdana",
        fontSize: "10pt",
        textAlign: "justify",
        marginLeft: "4cm",
        marginRight: "0",
        marginTop: "0.5cm",
        marginBottom: "0.5cm",
        textIndent: "0",
        lineHeight: "1.0",
      },
      li: {
        fontFamily: "Verdana",
        fontSize: "12pt",
        lineHeight: "1.5",
        marginBottom: "0.25cm",
      },
    },
  },

  juridico: {
    name: "Jurídico - Petições",
    description: "Formatação para petições e documentos jurídicos",
    font: { family: "Verdana", size: "12pt", color: "#000000" },
    lineSpacing: { normal: "1.5", citation: "1.0" },
    textAlign: "justify",
    paragraphIndent: "2cm",
    elements: {
      h1: {
        fontFamily: "Verdana",
        fontSize: "12pt",
        fontWeight: "bold",
        textTransform: "uppercase",
        textAlign: "center",
        marginTop: "1cm",
        marginBottom: "1cm",
        lineHeight: "1.5",
      },
      h2: {
        fontFamily: "Verdana",
        fontSize: "12pt",
        fontWeight: "bold",
        textTransform: "uppercase",
        textAlign: "left",
        marginTop: "1cm",
        marginBottom: "0.5cm",
        lineHeight: "1.5",
      },
      h3: {
        fontFamily: "Verdana",
        fontSize: "12pt",
        fontWeight: "bold",
        textAlign: "left",
        marginTop: "0.5cm",
        marginBottom: "0.5cm",
        lineHeight: "1.5",
      },
      p: {
        fontFamily: "Verdana",
        fontSize: "12pt",
        textAlign: "justify",
        textIndent: "2cm",
        marginTop: "0",
        marginBottom: "0",
        lineHeight: "1.5",
      },
      blockquote: {
        fontFamily: "Verdana",
        fontSize: "10pt",
        fontStyle: "italic",
        textAlign: "justify",
        marginLeft: "4cm",
        marginRight: "0",
        marginTop: "0.5cm",
        marginBottom: "0.5cm",
        textIndent: "0",
        lineHeight: "1.0",
      },
      // Endereçamento especial para petições
      enderecamento: {
        fontFamily: "Verdana",
        fontSize: "12pt",
        fontWeight: "bold",
        textTransform: "uppercase",
        textAlign: "center",
        marginBottom: "3cm",
        lineHeight: "1.5",
      },
      li: {
        fontFamily: "Verdana",
        fontSize: "12pt",
        lineHeight: "1.5",
      },
    },
  },

  contrato: {
    name: "Contratos",
    description: "Formatação para contratos e instrumentos particulares",
    font: { family: "Verdana", size: "12pt", color: "#000000" },
    lineSpacing: { normal: "1.5", citation: "1.0" },
    textAlign: "justify",
    paragraphIndent: "0",
    elements: {
      h1: {
        fontFamily: "Verdana",
        fontSize: "14pt",
        fontWeight: "bold",
        textTransform: "uppercase",
        textAlign: "center",
        marginBottom: "1cm",
      },
      h2: {
        fontFamily: "Verdana",
        fontSize: "12pt",
        fontWeight: "bold",
        textTransform: "uppercase",
        textAlign: "left",
        marginTop: "0.75cm",
        marginBottom: "0.25cm",
      },
      p: {
        fontFamily: "Verdana",
        fontSize: "12pt",
        textAlign: "justify",
        textIndent: "0",
        marginBottom: "0.5cm",
        lineHeight: "1.5",
      },
      blockquote: {
        fontFamily: "Verdana",
        fontSize: "11pt",
        textAlign: "justify",
        marginLeft: "2cm",
        lineHeight: "1.0",
      },
      li: {
        fontFamily: "Verdana",
        fontSize: "12pt",
        lineHeight: "1.5",
      },
    },
  },
};

/**
 * Get a formatting style by name
 */
export function getStyle(name: StyleName): FormattingStyle {
  return STYLES[name];
}

/**
 * Get all available styles
 */
export function getAllStyles(): Array<{ id: StyleName; name: string; description: string }> {
  return Object.entries(STYLES).map(([id, style]) => ({
    id: id as StyleName,
    name: style.name,
    description: style.description,
  }));
}

/**
 * Generate the HTML template string for an element
 */
export function getElementHtml(style: FormattingStyle, element: keyof FormattingStyle["elements"], content: string): string {
  const el = style.elements[element];
  if (!el) return content;

  const inlineStyle = elementToInlineStyle(el);
  const tag = element === "blockquote" ? "blockquote" : element;

  return `<${tag} style="${inlineStyle}">${content}</${tag}>`;
}

/**
 * Generate the AI prompt instructions for a given style
 */
export function getFormattingPrompt(styleName: StyleName): string {
  const style = STYLES[styleName];

  const h1Style = elementToInlineStyle(style.elements.h1);
  const h2Style = elementToInlineStyle(style.elements.h2);
  const pStyle = elementToInlineStyle(style.elements.p);
  const blockquoteStyle = elementToInlineStyle(style.elements.blockquote);
  const liStyle = style.elements.li ? elementToInlineStyle(style.elements.li) : "";

  let prompt = `INSTRUÇÕES DE FORMATAÇÃO HTML (${style.name.toUpperCase()}):

Gere o conteúdo usando HTML com estilos inline. Use EXATAMENTE estas formatações:

1. TÍTULO PRINCIPAL (h1):
   <h1 style="${h1Style}">Texto do Título</h1>

2. SUBTÍTULO/SEÇÃO (h2):
   <h2 style="${h2Style}">Texto do Subtítulo</h2>

3. PARÁGRAFO NORMAL:
   <p style="${pStyle}">Texto do parágrafo.</p>

4. CITAÇÃO LONGA (mais de 3 linhas):
   <blockquote style="${blockquoteStyle}">"Texto da citação..." (Autor, ano)</blockquote>

5. LISTA ORDENADA:
   <ol style="margin-left: 1.25cm;">
     <li style="${liStyle}">Item da lista</li>
   </ol>

6. NEGRITO e ITÁLICO:
   <strong>texto em negrito</strong>
   <em>texto em itálico</em>

IMPORTANTE:
- Use APENAS tags HTML com estilos inline (style="...")
- NÃO use markdown (**, ##, etc.)
- NÃO use CSS externo ou classes
- Fonte: ${style.font.family} ${style.font.size}
- Espaçamento: ${style.lineSpacing.normal} para texto, ${style.lineSpacing.citation} para citações
- Alinhamento: ${style.textAlign}
- Recuo de parágrafo: ${style.paragraphIndent}
`;

  // Add special instructions for juridico style
  if (styleName === "juridico") {
    const endStyle = style.elements.enderecamento
      ? elementToInlineStyle(style.elements.enderecamento)
      : "";

    prompt += `
ELEMENTOS ESPECIAIS PARA PETIÇÕES:

ENDEREÇAMENTO (no início):
<p style="${endStyle}">EXCELENTÍSSIMO SENHOR DOUTOR JUIZ DE DIREITO DA ___ VARA...</p>

SEÇÕES NUMERADAS EM ROMANOS:
- I - DOS FATOS
- II - DO DIREITO
- III - DOS PEDIDOS

CITAÇÕES DE JURISPRUDÊNCIA em itálico:
<blockquote style="${blockquoteStyle}">"Ementa..." (STJ, REsp nº XXX)</blockquote>
`;
  }

  return prompt;
}

/**
 * Get default style (ABNT)
 */
export function getDefaultStyle(): FormattingStyle {
  return STYLES.abnt;
}

/**
 * Get default style name
 */
export function getDefaultStyleName(): StyleName {
  return "abnt";
}
